import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { APP_URL } from "@/lib/constants.ts";
import { $FIXME } from "@/interfaces";
import { ChatRoomFileType } from "@/models/chat.model.ts";
import { toast } from "sonner";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getPageRange(current: number, total: number): (number | "...")[] {
  const delta = 2;
  const range: (number | "...")[] = [];
  const left = Math.max(0, current - delta);
  const right = Math.min(total - 1, current + delta);

  if (left > 0) {
    range.push(0);
    if (left > 1) range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < total - 1) {
    if (right < total - 2) range.push("...");
    range.push(total - 1);
  }

  return range;
}

export function getPaginationRange(page: number, pageSize: number): [number, number] {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  return [from, to];
}

export function capitalize(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function getInitials(name: string) {
  if (!name) return "";
  return name
    .split(" ")
    .map((word) => word[0]?.toUpperCase())
    .join("")
    .slice(0, 2);
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

export function mapMimeToFileType(mime: string): ChatRoomFileType {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";

  const docMimes = ["application/pdf", "application/msword", "application/vnd", "text/plain"];
  if (docMimes.some((m) => mime.startsWith(m))) return "document";

  return "other";
}

export function appUrl(path: string = ""): string {
  if (!path) {
    return APP_URL.replace(/\/+$/, "");
  }

  const base = APP_URL.replace(/\/+$/, "");
  const segment = path.replace(/^\/+/, "");

  return `${base}/${segment}`;
}

export function getGeodecoded(obj: $FIXME) {
  const getContextValue = (context: any[], type: string) => context?.find((c) => c.id.startsWith(type + "."))?.text;
  const feature = obj?.features?.[0];
  const [longitude, latitude] = feature.geometry?.coordinates ?? [null, null];

  return {
    street: `${feature?.address ?? ""} ${feature?.text ?? ""}`.trim(),
    city: getContextValue(feature.context, "place"),
    zip: getContextValue(feature.context, "postcode"),
    state: getContextValue(feature.context, "region"),
    county: getContextValue(feature.context, "district"),
    longitude,
    latitude,
  };
}

export function toastSuccess(description: string) {
  return toast.success("Success", {
    description,
  });
}

export function toastError(description: string) {
  return toast.error("Error", {
    description,
  });
}
